import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "children", "count", "hovered", "iconLeft", "iconRight", "onBlur", "onClick", "onOpenChange", "open", "title", "value", "role", "disabled", "truncate", "truncateStringProps", "tabIndex", "linkComponent"],
  _excluded2 = ["hovered"];
import { Children, isValidElement, useMemo } from 'react';
import styled from 'foundations-theming/utils';
import { parentStyles, linkStyles, hoveredStyles, disabledStyles } from '../SideNav/SideNavItem.helpers';
import { SideNavContextProvider, useSideNavContext } from '../SideNav/SideNavContext';
import { HoverProvider } from '../../utils/useHovered';
import { NavItemLabel } from '../NavItemLabel/NavItemLabel';
import { isRenderable } from '../../utils/functions';
import { uniqueId } from '../../utils/uniqueId';
import { Controllable } from '../Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { NAV_LIST_TRANSITION_TIMING } from '../../../constants/times';
import { activeStylesObject } from './NavListItem.helpers';
import { useFolderNavContext } from '../FolderNav/FolderNavContext';
import { TruncateString } from '../TruncateString/TruncateString';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { AccordionTransition } from '../AccordionTransition';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const expandableContentId = uniqueId('NavListItem-expandable-content-');
const StyledUl = styled.ul.withConfig({
  displayName: "NavListItem__StyledUl"
})(({
  theme
}) => ({
  listStyle: 'none',
  paddingInlineStart: theme.space['0'],
  marginBlockEnd: theme.space['100']
}));
export const getStyledLinkCss = ({
  $active,
  $depth,
  $disabled,
  $hasChildren,
  $isFolderNav,
  $hovered,
  $cssStyles,
  theme
}) => {
  return Object.assign({}, linkStyles({
    theme,
    disabled: $disabled || false,
    $isFolderNav: true
  }), $active ? activeStylesObject({
    theme
  }) : {}, {
    [CSS_SPECIFICITY.OVERRIDE]: Object.assign({}, $hovered && !$disabled ? hoveredStyles({
      theme,
      $isFolderNav
    }) : undefined, $disabled ? disabledStyles({
      theme
    }) : undefined)
  }, getFocusStyleObject({
    theme
  }), $hasChildren ? parentStyles : undefined, {
    paddingLeft: parseInt(theme.space['300'], 10) * ($depth + 1),
    '&& > [data-icon-name]:first-child': {
      marginRight: theme.space['200'],
      marginBottom: theme.space['100']
    },
    '-webkit-font-smoothing': 'auto',
    '-moz-osx-font-smoothing': 'auto'
  }, $cssStyles);
};
const StyledLink = styled.a.withConfig({
  displayName: "NavListItem__StyledLink"
})(props => getStyledLinkCss(props));
const InternalNavListItem = props => {
  const {
      active: __active = false,
      children,
      count
      // duration,
      ,

      iconLeft: IconLeft,
      iconRight: IconRight
      // itemClassName,
      // linkComponent,
      ,

      onClick,
      onOpenChange,
      open = false,
      title,
      // adding this because we're not using Link anymore, so we're not getting
      // the button role for free with no href
      role = 'button',
      disabled: __disabled,
      truncate = false,
      truncateStringProps,
      tabIndex = 0,
      linkComponent: LinkComponent = StyledLink
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    isFolderNav
  } = useFolderNavContext();
  const {
    tokenProps,
    domProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    value: navValue,
    depth
  } = useSideNavContext();
  const active = __active || navValue === props.value;
  const hasChildren = isRenderable(children);
  const _expandableContentId = useMemo(() => uniqueId('select-'), []);
  const handleClick = evt => {
    if (__disabled) {
      evt.stopPropagation();
      evt.preventDefault();
      return;
    }
    if (onClick) onClick(evt);
    if (!evt.defaultPrevented && onOpenChange) {
      onOpenChange(syntheticEvent(!open));
    }
  };
  const handleKeyDown = evt => {
    if (evt.key === ' ' || evt.key === 'Space') {
      if (__disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      // @ts-expect-error - KeyboardEvent is not the same as MouseEvent - https://git.hubteam.com/HubSpot/UIComponents/blob/853cf6d1f0b241d4e59d25f3f9ef0fcdb5ab44c8/UIComponents/static/js/link/UILink.tsx#L186
      handleClick(evt);

      // Prevent scrolling
      evt.preventDefault();
    }
  };
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, props, {
    children: hoverProviderProps => {
      const {
          hovered: _hovered
        } = hoverProviderProps,
        hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
      return /*#__PURE__*/_jsxs("li", {
        children: [/*#__PURE__*/_jsxs(LinkComponent, Object.assign({
          $active: active,
          $hasChildren: hasChildren,
          $hovered: _hovered || undefined,
          $depth: depth,
          $disabled: __disabled || false,
          $cssStyles: cssStyles,
          $isFolderNav: isFolderNav,
          "aria-expanded": hasChildren ? open : undefined,
          "aria-controls": hasChildren ? _expandableContentId : undefined,
          "aria-disabled": __disabled || false
        }, domProps, restProps, hoverProviderRestProps, {
          "aria-current": active ? 'true' : undefined,
          onClick: handleClick,
          onKeyDown: handleKeyDown,
          role: role,
          tabIndex: __disabled ? -1 : tabIndex,
          children: [typeof IconLeft === 'function' ? IconLeft({
            open
          }) : IconLeft, /*#__PURE__*/_jsx(NavItemLabel, {
            count: count,
            children: truncate ? /*#__PURE__*/_jsx(TruncateString, Object.assign({}, truncateStringProps, {
              children: title
            })) : title
          }), IconRight && (typeof IconRight === 'function' ? IconRight({
            open
          }) : IconRight)]
        })), hasChildren && /*#__PURE__*/_jsx(AccordionTransition, {
          id: expandableContentId,
          open: open,
          duration: parseInt(NAV_LIST_TRANSITION_TIMING, 10),
          children: /*#__PURE__*/_jsx(StyledUl, {
            children: Children.map(children, child => {
              if ( /*#__PURE__*/isValidElement(child)) {
                return /*#__PURE__*/_jsx(SideNavContextProvider, {
                  depth: depth + 1,
                  value: navValue,
                  children: child
                });
              }
            })
          })
        })]
      });
    }
  }));
};
const defaultProps = {
  open: false
};
export const NavListItem = Controllable(InternalNavListItem, ['open'], defaultProps);
NavListItem.displayName = 'NavListItem';